import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getApiUrl } from '../../config/api';
import NotificationPopover from './NotificationPopover';
import { Box, Typography, IconButton, Avatar, InputBase, Paper, Badge, useTheme, useMediaQuery } from '@mui/material';
import {
    Search as SearchIcon,
    NotificationsNone as NotificationsNoneIcon,
    Menu as MenuIcon,
} from '@mui/icons-material';
import { useSchoolProfile } from '../../hooks/useSchoolProfile';

const Header = ({ onMenuClick }: { onMenuClick?: () => void }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { profile, loading: profileLoading } = useSchoolProfile();
    const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');

    // Notifications State
    const [notifications, setNotifications] = useState<any[]>([]);
    const [notifLoading, setNotifLoading] = useState(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const fetchNotifications = useCallback(async () => {
        try {
            const token = userInfo.token;
            if (!token) return;

            setNotifLoading(true);
            const res = await axios.get(getApiUrl('notifications'), {
                headers: { Authorization: `Bearer ${token}` }
            });
            setNotifications(res.data);
        } catch (error) {
            console.error('Failed to fetch notifications:', error);
        } finally {
            setNotifLoading(false);
        }
    }, [userInfo.token]);

    useEffect(() => {
        fetchNotifications();
        const interval = setInterval(fetchNotifications, 30000); // Poll every 30s
        return () => clearInterval(interval);
    }, [fetchNotifications]);

    const handleNotifClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleNotifClose = () => {
        setAnchorEl(null);
    };

    const handleMarkAsRead = async (id: string) => {
        try {
            await axios.put(getApiUrl(`notifications/${id}/read`), {}, {
                headers: { Authorization: `Bearer ${userInfo.token}` }
            });
            fetchNotifications();
        } catch (error) {
            console.error('Failed to mark as read:', error);
        }
    };

    const handleClearAll = async () => {
        if (!window.confirm('Clear all notifications?')) return;
        try {
            await axios.delete(getApiUrl('notifications'), {
                headers: { Authorization: `Bearer ${userInfo.token}` }
            });
            setNotifications([]);
        } catch (error) {
            console.error('Failed to clear notifications:', error);
        }
    };

    const unreadCount = notifications.filter(n => !n.isRead).length;

    return (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 5 }}>
            {/* Profile and Breadcrumbs */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, md: 3 } }}>
                {isMobile && (
                    <IconButton
                        onClick={onMenuClick}
                        sx={{ mr: 1, color: '#1a237e', bgcolor: 'rgba(26, 35, 126, 0.05)' }}
                    >
                        <MenuIcon />
                    </IconButton>
                )}
                <Box sx={{ display: isSmallMobile ? 'none' : 'block' }}>
                    <Typography
                        variant={isMobile ? "subtitle1" : "h5"}
                        fontWeight={800}
                        color="#1a237e"
                        sx={{ lineHeight: 1.2 }}
                    >
                        {profileLoading ? '...' : (profile?.name || 'School System')}
                    </Typography>
                    {!isMobile && (
                        <Typography variant="caption" color="text.secondary">
                            {profile?.phone && `Phone: ${profile.phone} | `}
                            {profile?.email && `Email: ${profile.email}`}
                        </Typography>
                    )}
                </Box>
            </Box>

            {/* Actions */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, md: 3 } }}>
                {!isSmallMobile && (
                    <Paper
                        elevation={0}
                        sx={{
                            p: '4px 12px',
                            display: 'flex',
                            alignItems: 'center',
                            width: { xs: 150, md: 300 },
                            bgcolor: '#f8f9fa',
                            borderRadius: '12px'
                        }}
                    >
                        <SearchIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 20 }} />
                        <InputBase
                            sx={{ flex: 1, fontSize: '0.9rem' }}
                            placeholder={isMobile ? "Search..." : "Search student, staff or classes..."}
                        />
                    </Paper>
                )}

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <IconButton
                        onClick={handleNotifClick}
                        sx={{ bgcolor: '#F4F7FE', p: 1.2, borderRadius: '14px', '&:hover': { bgcolor: '#E0E5F2' } }}
                    >
                        <Badge badgeContent={unreadCount} color="error" overlap="circular">
                            <NotificationsNoneIcon sx={{ fontSize: 22, color: '#1B2559' }} />
                        </Badge>
                    </IconButton>

                    <NotificationPopover
                        open={Boolean(anchorEl)}
                        anchorEl={anchorEl}
                        onClose={handleNotifClose}
                        notifications={notifications}
                        loading={notifLoading}
                        onMarkAsRead={handleMarkAsRead}
                        onClearAll={handleClearAll}
                    />

                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        pl: 2,
                        ml: 1,
                        borderLeft: '1px solid #E0E5F2',
                        cursor: 'pointer'
                    }}>
                        {!isMobile && (
                            <Box sx={{ textAlign: 'right' }}>
                                <Typography variant="body2" fontWeight={800} sx={{ color: '#1B2559', lineHeight: 1 }}>
                                    {userInfo.name || 'Admin'}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#A3AED0', fontSize: '0.75rem' }}>
                                    {userInfo.role?.replace('_', ' ') || 'Manager'}
                                </Typography>
                            </Box>
                        )}
                        <Avatar
                            alt={userInfo.name}
                            src={userInfo.avatar}
                            sx={{
                                width: 44,
                                height: 44,
                                borderRadius: '16px',
                                background: 'linear-gradient(135deg, #1a237e 0%, #0B0F19 100%)',
                                fontWeight: 800,
                                boxShadow: '0 8px 16px rgba(26, 35, 126, 0.2)'
                            }}
                        >
                            {userInfo.name ? userInfo.name.charAt(0) : 'S'}
                        </Avatar>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default Header;
